import { FC, useEffect, useMemo, useState } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";
import { isEqual } from "lodash";
import { usePostHog } from "posthog-js/react";

import {
  Button,
  ErrorMessage,
  Modal,
  TermsAndConditions,
  TransactionFeeNotice,
  WarningMessage,
} from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { usePrevious } from "hooks";
import {
  formatAmount,
  formatCurrencyAmount,
  formatDate,
  formatNumber,
} from "lib/format";
import { parseNumber } from "lib/parse";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { BaseAmountSource } from "services/listings/types";
import { makeOffer } from "services/offers";
import { ListingOffer } from "services/offers/types";
import { createProxy, deleteProxy } from "services/proxies";
import { Proxy } from "services/proxies/types";
import { Track } from "services/valuation/types";
import theme from "theme";
import { circleIconSize } from "theme/constants";

import MakeOfferForm from "./MakeOfferForm";

interface MakeOfferModalProps {
  open: boolean;
  onClose: () => void;
  buyNowPrice: number;
  dollarAge: number;
  highestOpenBuyerOfferAmount: number;
  isAcceptingFinalOffers: boolean;
  isAcceptingFinalOffersEnabled: boolean;
  isMarketplaceMedianDisabled: boolean;
  listingId: number;
  listingProxyIncrementAmount: number;
  lte: number;
  ltm: number;
  marketplaceMedian: string | null;
  marketplaceMedianMultiplier: number;
  marketplaceMedianSource: BaseAmountSource;
  minimumPrice: number;
  offers: ListingOffer[];
  openProxy: Proxy | undefined;
  termExpirationDate: string | null;
  trackList: Track[];
  userActiveOffer: ListingOffer;
  valuationId: number;
}

const titleByStep = [
  "Make Offer",
  "Confirm Offer",
  "Offer Confirmed",
  "Offer Rejected",
];

const MakeOfferModal: FC<MakeOfferModalProps> = ({
  open,
  onClose,
  buyNowPrice,
  dollarAge,
  highestOpenBuyerOfferAmount,
  isAcceptingFinalOffers,
  isAcceptingFinalOffersEnabled,
  isMarketplaceMedianDisabled,
  listingId,
  listingProxyIncrementAmount,
  lte,
  ltm,
  marketplaceMedian,
  marketplaceMedianMultiplier,
  marketplaceMedianSource,
  minimumPrice,
  offers,
  openProxy,
  termExpirationDate,
  trackList,
  userActiveOffer,
  valuationId,
}) => {
  const initialMakeOfferValues = {
    offerAmount: userActiveOffer?.amount || "",
    proxyAmount: openProxy?.amount,
  };
  const {
    listing,
    handleRefreshListing,
    handleRefreshProxy,
  } = useAssetDetailData(listingId);
  const posthog = usePostHog();

  const [step, setStep] = useState(0);
  const [values, setValues] = useState(initialMakeOfferValues);
  const [isLoading, setIsLoading] = useState(false);
  const [statisticsChanged, setStatisticsChanged] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const statistics = useMemo(
    () => ({
      lte,
      tracklistLength: trackList.length,
      buyNowPrice,
      offers,
    }),
    [lte, trackList, buyNowPrice, offers],
  );
  const previousStatistics = usePrevious(statistics);

  useEffect(() => {
    if (!!previousStatistics && !isEqual(statistics, previousStatistics)) {
      setStatisticsChanged(true);
    }
  }, [statistics, previousStatistics]);

  useEffect(() => {
    setValues(prev => ({
      ...prev,
      proxyAmount: openProxy?.amount,
    }));
  }, [openProxy?.amount]);

  const makeOnBackHandler = () => {
    if (step === 1) {
      return () => {
        setStatisticsChanged(false);
        setErrorMessage("");
        setStep(s => s - 1);
      };
    } else {
      return undefined;
    }
  };

  const handleMakeOffer = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      if (!isEqual(values.proxyAmount, initialMakeOfferValues.proxyAmount)) {
        if (!!values.proxyAmount) {
          await createProxy({
            listing_id: listing.id,
            amount: parseNumber(values.proxyAmount),
          });
        } else if (!!openProxy) {
          await deleteProxy(openProxy.id);
        }
        handleRefreshProxy();
      }
      let state = "";
      if (!userActiveOffer) {
        const res = await makeOffer(
          listing.id,
          {
            valuation_id: valuationId,
            amount: parseNumber(values.offerAmount),
          },
          posthog,
        );
        state = res.state;
      }
      setStep(s => (state.startsWith("fail_") ? s + 2 : s + 1));
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE);
      setIsLoading(false);
    }
  };

  const handleRefreshListingWithLoadingState = async () => {
    try {
      setIsLoading(true);
      await handleRefreshListing();
    } catch (e) {
      // pass
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Modal
      title={titleByStep[step]}
      open={open}
      onClose={onClose}
      onBack={makeOnBackHandler()}
    >
      {step === 0 && (
        <MakeOfferForm
          buyNowPrice={buyNowPrice}
          highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
          initialValues={values}
          isAcceptingFinalOffers={isAcceptingFinalOffers}
          isAcceptingFinalOffersEnabled={isAcceptingFinalOffersEnabled}
          isMarketplaceMedianDisabled={isMarketplaceMedianDisabled}
          listingProxyIncrementAmount={listingProxyIncrementAmount}
          ltm={ltm}
          marketplaceMedian={marketplaceMedian}
          marketplaceMedianMultiplier={marketplaceMedianMultiplier}
          marketplaceMedianSource={marketplaceMedianSource}
          minimumPrice={minimumPrice}
          onSave={async v => {
            setValues(v);
            await handleRefreshListingWithLoadingState();
          }}
          onSaveSuccess={() => setStep(s => s + 1)}
          userActiveOffer={userActiveOffer}
        />
      )}
      {step === 1 && (
        <>
          <Typography component="div" gutterBottom>
            <Box fontSize={13} textAlign="center">
              The seller will be immediately notified of your offer.
            </Box>
          </Typography>
          <Box m={2} textAlign="center">
            <Box fontSize={18} m={1}>
              Your Offer Amount
            </Box>
            <Box
              fontSize={18}
              fontWeight={theme.typography.fontWeightMedium}
              m={1}
            >
              {formatCurrencyAmount("USD", parseNumber(values.offerAmount))}
              {!!values.proxyAmount && (
                <>
                  <br />
                  <Box component="span" color={theme.palette.text.secondary}>
                    {` (Proxy: ${formatCurrencyAmount(
                      "USD",
                      parseNumber(values.proxyAmount),
                    )})`}
                  </Box>
                </>
              )}
            </Box>
          </Box>
          <Divider />
          <Box m={2} textAlign="center">
            <Typography component="div">
              {statisticsChanged && (
                <WarningMessage
                  title="Statistics Changed"
                  description={
                    "The earning statistics have changed for " +
                    "this catalog. Please check to confirm before " +
                    "proceeding."
                  }
                />
              )}
              {errorMessage && (
                <Box my={2}>
                  <ErrorMessage
                    title="Request Failed"
                    description={errorMessage}
                  />
                </Box>
              )}
              <Box fontSize={13} textAlign="center" m={2}>
                Confirmed Earning Statistics
              </Box>
              <Box m={2}>
                <Box fontSize={15}>Earns Through</Box>
                <Box
                  fontSize={15}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  {formatDate(termExpirationDate)}
                </Box>
              </Box>
              {!isAcceptingFinalOffers && (
                <Box m={2}>
                  <Box fontSize={15}>List Price</Box>
                  <Box
                    fontSize={15}
                    fontWeight={theme.typography.fontWeightMedium}
                  >
                    {formatCurrencyAmount("USD", buyNowPrice || null)}
                  </Box>
                </Box>
              )}
              <Box m={2}>
                <Box fontSize={15}>Last 12 Months&apos; Earnings</Box>
                <Box
                  fontSize={15}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  {formatAmount(ltm)}
                </Box>
              </Box>
              <Box m={2}>
                <Box fontSize={15}>Dollar Age</Box>
                <Box
                  fontSize={15}
                  fontWeight={theme.typography.fontWeightMedium}
                >
                  {formatNumber(dollarAge, " Years")}
                </Box>
              </Box>
            </Typography>
          </Box>
          <Box m={2} textAlign="center">
            <TransactionFeeNotice
              isBuyNow={false}
              price={parseNumber(values.offerAmount)}
            />
            <TermsAndConditions />
            <Box m={2}>
              <Button
                variant="contained"
                customColor="green"
                size="large"
                fullWidth
                onClick={handleMakeOffer}
                disabled={isLoading}
                isLoading={isLoading}
                id="confirm-offer"
              >
                Confirm Offer
              </Button>
            </Box>
          </Box>
        </>
      )}
      {step === 2 && (
        <Typography component="div">
          <Box textAlign="center" mb={2}>
            <CheckCircleIcon
              style={{
                fontSize: circleIconSize.big,
                color: theme.custom.palette.green.main,
              }}
            />
          </Box>
          <Box fontSize={15} textAlign="center" m={2}>
            Your offer has been confirmed to the seller.
          </Box>
        </Typography>
      )}
      {step === 3 && (
        <>
          <Typography component="div">
            <Box textAlign="center">
              <CancelIcon
                style={{
                  fontSize: circleIconSize.big,
                  color: theme.custom.palette.red.main,
                }}
              />
            </Box>
            <Box fontSize={15} textAlign="center" m={2}>
              Your offer was automatically rejected by the seller. They will not
              accept offers below {formatCurrencyAmount("USD", minimumPrice)}.
            </Box>
          </Typography>
          <Box textAlign="center">
            <Button
              variant="contained"
              customColor="green"
              fullWidth
              onClick={() => setStep(0)}
            >
              Make New Offer
            </Button>
          </Box>
        </>
      )}
    </Modal>
  );
};

export default MakeOfferModal;
